// src/pages/Achats/useAchatsListing.js
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { getReglements } from "./../../services/achatsServices";

import {
  buildAchatsCSV,
  buildRevenue12Months,
  downloadCSV,
  getBuyer,
  isPaidPurchase,
  normalizeText,
  parsePrice,
  toAchatDate,
  toDateOnly,
} from "./achatsUtils";

export function useAchatsListing() {
  const [achats, setAchats] = useState([]);
  const [loading, setLoading] = useState(true);

  const [q, setQ] = useState("");
  const [formule, setFormule] = useState("all");
  const [paidOnly, setPaidOnly] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState("date_desc");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const loadAchats = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getReglements();
      setAchats(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur chargement achats :", error);
      toast.error("Erreur lors du chargement des achats.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAchats();
  }, [loadAchats]);

  const formuleOptions = useMemo(() => {
    const options = new Set();

    for (const achat of achats) {
      if (achat?.formule) {
        options.add(achat.formule);
      }
    }

    return ["all", ...Array.from(options).sort((a, b) => a.localeCompare(b))];
  }, [achats]);

  const filteredAchats = useMemo(() => {
    let nextAchats = [...achats];

    const search = normalizeText(q.trim());

    if (search) {
      nextAchats = nextAchats.filter((achat) => {
        const user = getBuyer(achat);

        const searchable = [
          achat.id,
          achat.formule,
          achat.statut_reglement,
          achat.payment_status,
          achat.prix_total,
          user.nom,
          user.prenom,
          user.email,
        ]
          .map(normalizeText)
          .join(" ");

        return searchable.includes(search);
      });
    }

    if (formule !== "all") {
      nextAchats = nextAchats.filter((achat) => achat.formule === formule);
    }

    if (paidOnly) {
      nextAchats = nextAchats.filter(isPaidPurchase);
    }

    if (dateFrom) {
      const from = new Date(`${dateFrom}T00:00:00`);

      nextAchats = nextAchats.filter((achat) => {
        const date = toDateOnly(achat.date_achat);
        return date && date >= from;
      });
    }

    if (dateTo) {
      const to = new Date(`${dateTo}T23:59:59`);

      nextAchats = nextAchats.filter((achat) => {
        const date = toAchatDate(achat.date_achat);
        return date && date <= to;
      });
    }

    nextAchats.sort((a, b) => {
      if (sort === "date_desc") {
        return (
          (toAchatDate(b.date_achat)?.getTime() || 0) -
          (toAchatDate(a.date_achat)?.getTime() || 0)
        );
      }

      if (sort === "date_asc") {
        return (
          (toAchatDate(a.date_achat)?.getTime() || 0) -
          (toAchatDate(b.date_achat)?.getTime() || 0)
        );
      }

      if (sort === "amount_desc") {
        return parsePrice(b.prix_total) - parsePrice(a.prix_total);
      }

      if (sort === "amount_asc") {
        return parsePrice(a.prix_total) - parsePrice(b.prix_total);
      }

      if (sort === "name_az") {
        return String(getBuyer(a).nom || "").localeCompare(
          String(getBuyer(b).nom || ""),
          "fr"
        );
      }

      if (sort === "name_za") {
        return String(getBuyer(b).nom || "").localeCompare(
          String(getBuyer(a).nom || ""),
          "fr"
        );
      }

      return 0;
    });

    return nextAchats;
  }, [achats, q, formule, paidOnly, dateFrom, dateTo, sort]);

  const pageCount = Math.max(1, Math.ceil(filteredAchats.length / pageSize));

  useEffect(() => {
    if (page > pageCount) {
      setPage(1);
    }
  }, [page, pageCount]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredAchats.slice(start, start + pageSize);
  }, [filteredAchats, page, pageSize]);

  const stats = useMemo(() => {
    const totalRevenue = filteredAchats.reduce(
      (sum, achat) => sum + parsePrice(achat.prix_total),
      0
    );

    const paidRows = filteredAchats.filter(isPaidPurchase);

    const paidRevenue = paidRows.reduce(
      (sum, achat) => sum + parsePrice(achat.prix_total),
      0
    );

    const pendingRows = filteredAchats.filter((achat) => !isPaidPurchase(achat));

    const averageBasket =
      filteredAchats.length > 0 ? totalRevenue / filteredAchats.length : 0;

    const proRows = filteredAchats.filter((achat) =>
      normalizeText(achat.formule).includes("pro")
    );

    const ultimRows = filteredAchats.filter((achat) =>
      normalizeText(achat.formule).includes("ultim")
    );

    const lastPurchase = [...filteredAchats].sort(
      (a, b) =>
        (toAchatDate(b.date_achat)?.getTime() || 0) -
        (toAchatDate(a.date_achat)?.getTime() || 0)
    )[0];

    return {
      count: filteredAchats.length,
      totalRevenue,
      paidCount: paidRows.length,
      paidRevenue,
      pendingCount: pendingRows.length,
      averageBasket,
      proCount: proRows.length,
      proRevenue: proRows.reduce(
        (sum, achat) => sum + parsePrice(achat.prix_total),
        0
      ),
      ultimCount: ultimRows.length,
      ultimRevenue: ultimRows.reduce(
        (sum, achat) => sum + parsePrice(achat.prix_total),
        0
      ),
      lastPurchase,
    };
  }, [filteredAchats]);

  const revenue12 = useMemo(
    () => buildRevenue12Months(filteredAchats),
    [filteredAchats]
  );

  const exportCSV = useCallback(() => {
    const csv = buildAchatsCSV(filteredAchats);
    downloadCSV("achats-monapplitournoi.csv", csv);
    toast.success("Export CSV généré.");
  }, [filteredAchats]);

  const resetFilters = useCallback(() => {
    setQ("");
    setFormule("all");
    setPaidOnly(false);
    setDateFrom("");
    setDateTo("");
    setSort("date_desc");
    setPage(1);
  }, []);

  return {
    achats,
    filteredAchats,
    pageRows,
    stats,
    revenue12,
    formuleOptions,

    loading,

    q,
    setQ,
    formule,
    setFormule,
    paidOnly,
    setPaidOnly,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    sort,
    setSort,

    page,
    setPage,
    pageSize,
    setPageSize,
    pageCount,

    loadAchats,
    exportCSV,
    resetFilters,
  };
}